import { type QuestionDetail } from "@app/screens/ExternalQuestionnaires/externalQuestionnaire/types";
import { SupportedLocales } from "@app/shared/utils/locales";
import { QuestionProof, QuestionType } from "@generated/client/graphql";
import { useEffect, useState } from "react";
import { computeQuestionInput } from "../../../../services";
import { type Option as TOption, type UpsertQuestionInput } from "../../types";

import { useCurrentUserLocale } from "@app/shared/utils/currentUserLocale";
import { type QuestionUpdateInput } from "@generated/client/graphql";
import { isEmpty } from "lodash";
import { type ChangeEvent } from "react";
import { useQuestionnaireLocale } from "../../../../data";
import { computeMetadata } from "../../../../services";

export const useQuestionForm = (
    initialQuestion?: QuestionDetail,
    questionnaireId?: string,
    {
        withProof = false,
        withNa = false,
    }: { withProof?: boolean; withNa?: boolean } = {},
) => {
    const curentUserlocale = useCurrentUserLocale(SupportedLocales.FR);
    const { availableLocales, defaultLocale } =
        useQuestionnaireLocale(questionnaireId);
    const [locale, setLocale] = useState<SupportedLocales>(
        defaultLocale ?? curentUserlocale,
    );

    const [questionInput, setQuestionInput] = useState<UpsertQuestionInput>(
        computeQuestionInput(initialQuestion, withProof, withNa),
    );

    const [createMoreQuestions, setCreateMoreQuestions] = useState(false);

    const updateField = (
        field: keyof UpsertQuestionInput,
        value?:
            | string
            | string[]
            | QuestionType
            | TOption[]
            | { questionId?: string; displayIfSelected?: number[] }
            | QuestionProof
            | boolean,
    ) => {
        setQuestionInput((prev) => ({ ...prev, [field]: value }));
    };

    const updateLocaleField = (
        field: "nameMultiLocale" | "descriptionMultiLocale",
        value: string,
    ) => {
        setQuestionInput((prev) => ({
            ...prev,
            [field]: { ...prev[field], [locale]: value },
        }));
    };

    const resetForm = () => {
        setQuestionInput({
            nameMultiLocale: { en: "", fr: "" },
            descriptionMultiLocale: { en: "", fr: "" },
            type: QuestionType.Text,
            unit: undefined,
            options: [],
            dependsOn: {
                questionId: undefined,
                displayIfSelected: undefined,
            },
            proof: withProof ? QuestionProof.NotAllowed : undefined,
        });
        setCreateMoreQuestions(false);
    };

    useEffect(() => {
        if (availableLocales.includes(locale)) return;
        setLocale(defaultLocale);
    }, [defaultLocale, availableLocales, locale]);

    useEffect(() => {
        setQuestionInput(
            computeQuestionInput(initialQuestion, withProof, withNa),
        );
    }, [initialQuestion, withProof, withNa]);

    return {
        availableLocales,
        questionInput,
        locale,
        setLocale,
        updateField,
        updateLocaleField,
        resetForm,
        createMoreQuestions,
        setCreateMoreQuestions,
    };
};

export const createQuestionHandlers = (
    questionInput: UpsertQuestionInput,
    updateField: (
        field: keyof UpsertQuestionInput,
        value?:
            | string
            | string[]
            | QuestionType
            | TOption[]
            | { questionId?: string; displayIfSelected?: number[] }
            | QuestionProof
            | boolean,
    ) => void,
) => {
    const handleQuestionTypeChange = (type?: QuestionType) => {
        updateField("type", type);
    };

    const handleProofChange = (proof?: QuestionProof) => {
        updateField("proof", proof);
    };

    const handleOptionsChange = (
        opts: TOption[] | ((opts: TOption[]) => TOption[]),
    ) => {
        const updatedInput = {
            ...questionInput,
        };

        if (typeof opts === "function") {
            return updateField("options", opts(updatedInput.options));
        }
        updateField("options", opts);
    };

    const handleUnitChange = (e: ChangeEvent<HTMLInputElement>) => {
        updateField("unit", e.target.value);
    };

    const handleNaOptionChange = (enableNaOption?: boolean) => {
        updateField("enableNaOption", enableNaOption);
    };

    const handleIsRequiredOptionChange = (isRequired?: boolean) => {
        updateField("isRequired", isRequired);
    };

    return {
        handleQuestionTypeChange,
        handleProofChange,
        handleOptionsChange,
        handleUnitChange,
        handleNaOptionChange,
        handleIsRequiredOptionChange,
    };
};

export const constructQuestionInput = (
    questionInput: UpsertQuestionInput,
    locale: string,
    questionOrder: number,
    question?: QuestionDetail,
    questionnaireId?: string,
): QuestionUpdateInput => {
    if (!questionInput.type) {
        throw new Error("Question type is required");
    }

    return {
        name: questionInput.nameMultiLocale?.[locale] || "",
        nameMultiLocale: isEmpty(questionInput.nameMultiLocale)
            ? null
            : questionInput.nameMultiLocale,
        description: questionInput.descriptionMultiLocale?.[locale] || null,
        descriptionMultiLocale: isEmpty(questionInput.descriptionMultiLocale)
            ? null
            : questionInput.descriptionMultiLocale,
        type: questionInput.type,
        isRequired: questionInput.isRequired,
        metadata: computeMetadata({
            type: questionInput.type,
            options: questionInput.options,
            unit: questionInput.unit,
            parentQuestionId: questionInput?.dependsOn?.questionId,
            displayIfSelected: questionInput?.dependsOn?.displayIfSelected,
            proof: questionInput.proof,
            enableNaOption: questionInput.enableNaOption,
        }),
        ...(question?.id
            ? {}
            : {
                  questionnaireId,
                  order: questionOrder,
              }),
    };
};
